import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Message } from '../../types/chat';
import { DataTable } from './DataTable';
import { DataChart } from './DataChart';
import { DataHeatmap } from './DataHeatmap';

interface MessageBubbleProps {
  message: Message;
}

export const MessageBubble = ({ message }: MessageBubbleProps) => {
  const isUser = message.role === 'user';

  return (
    <div className={`mb-6 message-bubble flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`flex w-full gap-3 ${isUser ? 'justify-end' : 'justify-start'} md:max-w-[90%]`}
      >
        {/* Avatar */}
        {!isUser && (
          <div className="order-1 flex-shrink-0 w-8 h-8 rounded-xl bg-[#4c3794] flex items-center justify-center shadow-md">
            <span className="text-white text-xs font-bold">AI</span>
          </div>
        )}
        {isUser && (
          <div className="order-1 flex-shrink-0 w-8 h-8 rounded-xl bg-[#6a4caf] flex items-center justify-center shadow-md">
            <span className="text-white text-xs font-bold">U</span>
          </div>
        )}

        <div
          className={`order-2 rounded-2xl p-4 border max-w-[75%] ${
            isUser
              ? 'bg-gray-50 border-gray-200'
              : 'bg-white border-gray-100 shadow-sm'
          }`}
        >
          <div className="prose prose-sm max-w-none text-gray-900">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => <p className="text-sm leading-relaxed whitespace-pre-wrap break-words mb-2 last:mb-0">{children}</p>,
                ul: ({ children }) => <ul className="list-disc pl-5 mb-2 text-sm space-y-1">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 text-sm space-y-1">{children}</ol>,
                li: ({ children }) => <li className="mb-0.5 text-sm leading-relaxed">{children}</li>,
                code: ({ children, ...props }) => {
                  const match = /language-(\w+)/.exec(props.className || '');
                  const isInline = !match;
                  if (isInline) {
                    return <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono text-gray-900">{children}</code>;
                  }
                  return <pre className="bg-gray-100 p-3 rounded-lg overflow-x-auto my-2"><code className="text-xs font-mono text-gray-900">{children}</code></pre>;
                },
                h1: ({ children }) => <h1 className="text-lg font-bold mb-2 text-gray-900">{children}</h1>,
                h2: ({ children }) => <h2 className="text-base font-bold mb-2 text-gray-900">{children}</h2>,
                h3: ({ children }) => <h3 className="text-sm font-semibold mb-2 text-gray-900">{children}</h3>,
                a: ({ children, href }) => <a href={href} className="text-blue-600 underline hover:text-blue-800" target="_blank" rel="noopener noreferrer">{children}</a>,
                blockquote: ({ children }) => <blockquote className="border-l-4 border-gray-300 pl-4 italic my-2 text-sm text-gray-700">{children}</blockquote>,
                strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                em: ({ children }) => <em className="italic">{children}</em>,
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>

        {message.visualization && (
          <div className="mt-2">
            {message.visualization.type === 'table' && (
              <DataTable data={message.visualization.data} />
            )}
            {message.visualization.type === 'chart' && (
              <DataChart data={message.visualization.data} />
            )}
            {message.visualization.type === 'heatmap' && (
              <DataHeatmap data={message.visualization.data} />
            )}
          </div>
        )}

          {message.sourceLinks && message.sourceLinks.length > 0 && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="text-xs font-semibold text-green-900 mb-2">
                📄 Reference Documents
              </h4>
              <div className="space-y-1">
                {message.sourceLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-xs text-blue-600 hover:text-blue-800 underline break-all"
                  >
                    Document {index + 1}
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className={`mt-2 text-xs text-gray-400 ${isUser ? 'text-right' : 'text-left'}`}>
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>
    </div>
  );
};
